import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

const HERO = "https://images.unsplash.com/photo-1647628202317-e032dab055ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHNhbmQlMjBkdW5lcyUyMHRleHR1cmUlMjB3YXJtfGVufDB8fHx8MTc4NzgwNzMzNnww&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t("login.success"));
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-bone" data-testid="login-page">
      <div className="flex items-center justify-center p-8 sm:p-12 lg:p-24 relative">
        <div className="absolute top-4 end-4"><LanguageSwitcher /></div>
        <div className="w-full max-w-md animate-fade-up">
          <Link to="/" className="font-serif text-2xl font-bold text-midnight tracking-tight">Qafila</Link>
          <p className="overline text-gold mt-8">{t("login.welcome")}</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-midnight mt-2 tracking-tight">{t("login.title")}</h1>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-midnight">{t("common.email")}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" className="mt-2 rounded-none border-sand focus-visible:ring-gold" placeholder={t("login.emailPh")} />
            </div>
            <div>
              <Label htmlFor="password" className="text-midnight">{t("common.password")}</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" className="mt-2 rounded-none border-sand focus-visible:ring-gold" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-12 rounded-none bg-gold text-midnight hover:brightness-105 transition-all duration-300 font-medium">
              {loading ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-sand" />
            <span className="text-xs text-muted-foreground">{t("common.orDivider")}</span>
            <div className="h-px flex-1 bg-sand" />
          </div>

          <Button onClick={googleLogin} variant="outline" data-testid="login-google-btn"
            className="w-full h-12 rounded-none border-midnight text-midnight hover:bg-midnight hover:text-bone transition-all duration-300">
            {t("common.continueWithGoogle")}
          </Button>

          <p className="mt-8 text-sm text-muted-foreground">
            {t("login.noAccount")}{" "}
            <Link to="/register" className="text-midnight font-medium underline decoration-gold underline-offset-4" data-testid="to-register-link">{t("login.registerLink")}</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Sand texture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-midnight/40" />
        <div className="absolute bottom-16 start-12 end-12 text-bone">
          <p className="font-serif text-3xl leading-tight">{t("login.heroText")}</p>
        </div>
      </div>
    </div>
  );
}
